import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { Blade, Button, DataGrid } from "rhombus-react";
import { fetchAssignedCreatives } from "reducers/actions/ContentFeedActions";
import { AssignedCreatives } from "reducers/AssignedCreatives";
import { useGridScrollUtils } from "utils/DataGrid";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import { DEFAULT_ROW_HEIGHT } from "utils/constants";
import ProgressContainer from "widgets/ProgressContainer";
import { ASSIGNED_CREATIVES_COLUMNS } from "./AssignedCreativesColumns";

import "./ContentFeedAssignedCreativesModal.css";

export default function ContentFeedAssignedCreativesModal({
	onClose,
}) {
	const { libraryId, contentFeedId } = useParams();
	const location = useLocation();
	const contentFeedName = location.state?.contentFeedName;
	const creativeCount = location.state?.creativeCount || 0;
	const dispatch = useDispatch();
	const assignedCreatives = useSelector(state => state.assignedCreatives?.items || []);
	const isLoading = useSelector(state => state.assignedCreatives?.isLoading || false);
	const dataGridRef = useGridScrollUtils([]);
	const MAX_VISIBLE_ROWS = 8;
	const HEADER_ROW_HEIGHT = DEFAULT_ROW_HEIGHT;

	useTooltipRebuild(assignedCreatives);

	useEffect(() => {
		if (contentFeedId) {
			dispatch(fetchAssignedCreatives(contentFeedId));
		}

		return () => {
			dispatch(AssignedCreatives.clearAssignedCreatives());
		};
	}, [contentFeedId]);

	const mappedCreatives = useMemo(() => {
		return assignedCreatives.map(creative => ({
			...creative,
			libraryId,
			contentFeedId,
			contentFeedName,
		}));
	}, [assignedCreatives, libraryId, contentFeedId, contentFeedName]);

	function getRow(rowID) {
		if (rowID === -1) {
			return;
		}
		return mappedCreatives[rowID];
	}

	function calculateGridDimensions(actualRowsCount, predictedRowsCount) {
		const rowsCountForHeight = actualRowsCount || predictedRowsCount;
		const visibleRowsCount = Math.min(rowsCountForHeight, MAX_VISIBLE_ROWS);
		const gridHeight = HEADER_ROW_HEIGHT + (visibleRowsCount * DEFAULT_ROW_HEIGHT);
		const hasScroll = rowsCountForHeight > MAX_VISIBLE_ROWS;

		return {
			actualRowsCount,
			gridHeight,
			hasScroll,
			rowsCountForHeight,
		};
	}

	const { actualRowsCount, gridHeight, hasScroll, rowsCountForHeight } = calculateGridDimensions(mappedCreatives.length, creativeCount);

	const dataGridProps = {
		columns: ASSIGNED_CREATIVES_COLUMNS,
		rowGetter: getRow,
		rowsCount: actualRowsCount,
		rowHeight: DEFAULT_ROW_HEIGHT,
		enableCellAutoFocus: false,
		...(rowsCountForHeight > 0 && { minHeight: gridHeight }),
		...(hasScroll && { maxHeight: gridHeight }),
	};

	function renderContent() {
		const containerClassName = `assigned-creatives-grid-container${hasScroll ? " has-scroll" : ""}`;

		return (
			<ProgressContainer isLoading={isLoading} className="content-feed-assigned-creatives-body">
				<div ref={dataGridRef} className={containerClassName}>
					<DataGrid {...dataGridProps} />
				</div>
			</ProgressContainer>
		);
	}

	function renderFooter() {
		return (
			<Button theme="default" onClick={onClose}>
				Close
			</Button>
		);
	}

	const modalTitle = contentFeedName ? `Linked Creatives - ${contentFeedName}` : "Linked Creatives";

	return (
		<Blade
			type="modal"
			title={modalTitle}
			className="content-feed-assigned-creatives-modal"
			allowFullScreen={false}
			bladeProps={{ verticallyCenter: true }}
			xClose
			onClose={onClose}
			renderContent={renderContent}
			renderFooter={renderFooter}
			setAppTitle={false}
		/>
	);
}

ContentFeedAssignedCreativesModal.displayName = "ContentFeedAssignedCreativesModal";

